<!-- Tag field of a Vue dialog (hooks 'hook', sources 'src', saved videos 'saved'): one chip per tag of the chosen project (with its icon and
     colour) plus the chosen tags the project lacks; a tap chooses or removes it. "+" opens the "New tag" field
     (NewTagField.vue): Enter or ✓ creates the tag in the project (unless it exists already, without case) and chooses
     it, Esc or × cancels.
     The open field is TAGSTATE[kind].adding = 'picker' (src/stores/tags.ts), shared with the tag bars like the legacy
     picker of tags.js it replaced. Same markup as the legacy renderTagPicker.
     v-model: the chosen tags. flush() (through a template ref) creates and chooses a tag typed but not confirmed, and
     returns the chosen tags: call it when saving. -->
<script setup lang="ts">
import NewTagField from './NewTagField.vue'
import { chipCls } from '../../utils/chip'
import { computed, onBeforeUnmount, ref } from 'vue'
import LookIcon from '../LookIcon.vue'
import { useI18n } from '../../composables/useI18n'
import { useProfileData } from '../../composables/useProfileData'
import { useTagStore } from '../../stores/tags'
import { pickerTags, toggleTag } from '../../features/tags'
import { tagLook } from '../../features/looks'
import { hasTag } from '../../data/tags'
import type { TagGroup } from '../../data/types'

const props = defineProps<{ kind: TagGroup; projectId: string; modelValue: string[] }>()
const emit = defineEmits<{ 'update:modelValue': [tags: string[]] }>()
const { t } = useI18n()
const data = useProfileData()
const st = useTagStore().state[props.kind]

const list = computed(() => pickerTags(data.value, props.kind, props.projectId, props.modelValue))
const look = (tag: string) => tagLook(data.value, props.kind, tag, props.projectId)

// ===== New tag =====
if (st.adding === 'picker') st.adding = ''   // a field left open by another dialog
const field = ref<InstanceType<typeof NewTagField> | null>(null)
let chosen: string[] | null = null
function openNew() { st.adding = 'picker' }
/** The field created a tag (NewTagField.vue): it is chosen; `chosen` keeps the result for flush */
function onCreated(tag: string | null) {
  st.adding = ''
  let tags = props.modelValue
  if (tag && !hasTag(tags, tag)) { tags = [...tags, tag]; emit('update:modelValue', tags) }
  chosen = tags
}
function toggle(tag: string) { emit('update:modelValue', toggleTag(props.modelValue, tag)) }

/** A tag typed in the "New tag" field but not confirmed is still created and chosen (like the legacy pickedTags) */
function flush(): string[] {
  if (st.adding !== 'picker' || !field.value) return props.modelValue
  chosen = null; field.value.confirm()
  return chosen || props.modelValue
}
defineExpose({ flush })
onBeforeUnmount(() => { if (st.adding === 'picker') st.adding = '' })
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <button v-for="tag in list" :key="tag" type="button" :class="chipCls(hasTag(modelValue, tag))" @click="toggle(tag)"><span v-if="look(tag).icon" class="mr-1.5 inline-flex"><LookIcon :look="look(tag)" cls="text-[11px]" img="w-3.5 h-3.5" /></span>{{ tag }}</button>
    <NewTagField v-if="st.adding === 'picker'" ref="field" :kind="kind" :input-id="`newTag_${kind}_picker`" :pid="projectId" @done="onCreated" @cancel="st.adding = ''" />
    <button v-else type="button" :title="t('Create a new tag')" class="w-8 h-8 rounded-full border border-dashed border-gray-600 text-gray-400 hover:text-brand hover:border-brand text-xs" @click="openNew"><i class="fas fa-plus"></i></button>
  </div>
</template>
